/**
 * Pure helpers behind LlmPolicySettings.jsx — the tenant "LLM policy" admin
 * panel (GET/PUT /api/secrets/tenant/policy, GET /api/secrets/tenant/usage).
 * Kept out of the .jsx file so they can be unit-tested directly: this repo
 * runs vitest with `environment: 'node'` (vitest.config.js), so nothing in
 * the component itself can be rendered.
 */

// Mirrors llm-policy.js's EU_ONLY_REGIONS — the regions `euOnly` admits.
const EU_ONLY_REGIONS = new Set(['eu', 'local']);

/**
 * GET /api/secrets/tenant/usage's `share` (totalTokens / budget, null without
 * a budget, can exceed 1) as a 0–100 integer for a <Progress value max=100>
 * bar. Overshoot is clamped to 100 rather than left to overflow the bar.
 * @param {number|null} share
 * @returns {number|null}
 */
export function budgetSharePercent(share) {
  if (share == null || Number.isNaN(share)) return null;
  return Math.min(100, Math.max(0, Math.round(share * 100)));
}

/**
 * @param {number|null} share
 * @returns {'ok'|'warning'|'exceeded'|null}
 */
export function budgetSeverity(share) {
  if (share == null || Number.isNaN(share)) return null;
  if (share >= 1) return 'exceeded';
  if (share >= 0.8) return 'warning';
  return 'ok';
}

/**
 * Parses the "Monatsbudget in Tokens (leer = unbegrenzt)" field into what
 * llm-policy.js's normalizePolicy() accepts for `monthlyTokenBudget`: null
 * (unlimited) or a positive safe integer. Tolerates the thousands separators
 * (`.`, `,`, spaces) a human typing a six-digit token count would use.
 * @param {string|null|undefined} raw
 * @returns {{ value: number|null, error: string|null }}
 */
export function parseMonthlyBudgetInput(raw) {
  const text = (raw ?? '').toString().trim();
  if (text === '') return { value: null, error: null };

  const spaced = text.replace(/\s/g, '');
  // `.`/`,` only count as thousands separators in front of complete groups of
  // three digits (e.g. "500.000") — anything else (e.g. "12.5") is rejected
  // as a fraction rather than guessed at, since the server requires an
  // integer anyway.
  const grouped = /^\d{1,3}([.,]\d{3})+$/.test(spaced);
  const cleaned = grouped ? spaced.replace(/[.,]/g, '') : spaced;
  if (!/^\d+$/.test(cleaned)) {
    // i18n keys, not literal text — LlmPolicySettings.jsx (unlike
    // AccessTokens.jsx) follows the project's DE+EN i18n rule.
    return { value: null, error: 'admin.llmPolicyBudgetErrorFormat' };
  }
  const value = Number(cleaned);
  if (!Number.isSafeInteger(value) || value <= 0) {
    return { value: null, error: 'admin.llmPolicyBudgetErrorPositive' };
  }
  return { value, error: null };
}

/**
 * Which providers (from GET /api/settings/providers, each carrying
 * `dataRegion`) remain checked once "Nur EU-Verarbeitung" is on — mirrors
 * llm-policy.js's `isProviderAllowed()` euOnly gate for the *static* per-
 * provider fact (Ollama and OpenAI's actual region can differ per request;
 * see effectiveRegion() there). Purely explanatory copy, not an enforcement
 * decision — the server is what actually gates a session.
 * @param {Array<{ id: string, dataRegion?: string }>} providers
 * @returns {Array<{ id: string, dataRegion?: string }>}
 */
export function providersRemainingUnderEuOnly(providers) {
  return (providers || []).filter((p) => EU_ONLY_REGIONS.has(p?.dataRegion));
}

/**
 * Checkbox selection → llm-policy.js's `allowedProviders` field: null means
 * "no restriction", so selecting every known provider collapses to null
 * rather than an explicit list that would silently drop any provider added
 * later. An empty selection is kept as `[]` (not widened to "all") so the
 * server's own validation — "must name at least one provider" — is what the
 * admin sees, not a client that quietly ignored the choice.
 * @param {string[]} selectedIds
 * @param {string[]} allIds
 * @returns {string[]|null}
 */
export function normalizeAllowedProviders(selectedIds, allIds) {
  const selected = new Set(selectedIds || []);
  const all = allIds || [];
  if (all.length > 0 && all.every((id) => selected.has(id)) && selected.size === all.length) {
    return null;
  }
  return [...selected];
}
